// Anonymous device-bound progression. No accounts, no signup — a UUID stored
// in localStorage is the player's stable identity.

const DEVICE_KEY = 'smp.deviceId';
const STATS_KEY = 'smp.stats';
const UNLOCKS_KEY = 'smp.unlocks';
const HISTORY_KEY = 'smp.history';
const NAME_KEY = 'smp.username';

export type RunStats = {
  bestSurvivalSec: number;
  totalRunsPlayed: number;
  introvertWins: number;
  extrovertWins: number;
  totalTags: number;
  totalSurvivalSec: number;
  jobsCompleted: number;
  moneyEarned: number;
};

export type Unlocks = {
  hatColors: string[];      // unlocked cosmetic hat colors
  starterItems: string[];   // unlocked starter inventory items
  characters: string[];     // unlocked character variants
};

export type RunRecord = {
  ts: number;
  role: 'ext' | 'int';
  result: 'win' | 'loss' | 'tie';
  survivalSec: number;
  tags: number;
  moneyEarned: number;
};

const DEFAULT_STATS: RunStats = {
  bestSurvivalSec: 0,
  totalRunsPlayed: 0,
  introvertWins: 0,
  extrovertWins: 0,
  totalTags: 0,
  totalSurvivalSec: 0,
  jobsCompleted: 0,
  moneyEarned: 0,
};

const DEFAULT_UNLOCKS: Unlocks = {
  hatColors: ['blue', 'orange'],
  starterItems: ['snack'],
  characters: ['default'],
};

export function getDeviceId(): string {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = generateUuid();
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

export function getUsername(): string | null {
  return localStorage.getItem(NAME_KEY);
}

export function setUsername(name: string): void {
  localStorage.setItem(NAME_KEY, name);
}

export function getStats(): RunStats {
  try {
    const raw = localStorage.getItem(STATS_KEY);
    if (!raw) return { ...DEFAULT_STATS };
    return { ...DEFAULT_STATS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_STATS };
  }
}

export function saveStats(s: RunStats): void {
  localStorage.setItem(STATS_KEY, JSON.stringify(s));
}

export function getUnlocks(): Unlocks {
  try {
    const raw = localStorage.getItem(UNLOCKS_KEY);
    if (!raw) return { ...DEFAULT_UNLOCKS };
    return { ...DEFAULT_UNLOCKS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_UNLOCKS };
  }
}

export function saveUnlocks(u: Unlocks): void {
  localStorage.setItem(UNLOCKS_KEY, JSON.stringify(u));
}

export function getHistory(): RunRecord[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

export function recordRun(r: RunRecord): void {
  const history = getHistory();
  history.push(r);
  // Keep the last 100 runs only
  while (history.length > 100) history.shift();
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history));

  // Update aggregate stats
  const stats = getStats();
  stats.totalRunsPlayed++;
  stats.totalSurvivalSec += r.survivalSec;
  stats.totalTags += r.tags;
  stats.moneyEarned += r.moneyEarned;
  if (r.survivalSec > stats.bestSurvivalSec) stats.bestSurvivalSec = r.survivalSec;
  if (r.result === 'win') {
    if (r.role === 'int') stats.introvertWins++;
    else stats.extrovertWins++;
  }
  saveStats(stats);

  // Unlocks driven by milestones
  const unlocks = getUnlocks();
  let changed = false;
  if (stats.totalRunsPlayed >= 5 && !unlocks.hatColors.includes('green')) {
    unlocks.hatColors.push('green');
    changed = true;
  }
  if (stats.totalRunsPlayed >= 10 && !unlocks.starterItems.includes('coffee')) {
    unlocks.starterItems.push('coffee');
    changed = true;
  }
  if (stats.bestSurvivalSec >= 60 && !unlocks.characters.includes('hooded')) {
    unlocks.characters.push('hooded');
    changed = true;
  }
  if (stats.totalTags >= 25 && !unlocks.hatColors.includes('purple')) {
    unlocks.hatColors.push('purple');
    changed = true;
  }
  if (changed) saveUnlocks(unlocks);
}

export function recordJobComplete(payout: number): void {
  const stats = getStats();
  stats.jobsCompleted++;
  stats.moneyEarned += payout;
  saveStats(stats);
}

function generateUuid(): string {
  // RFC 4122 v4 — uses crypto.getRandomValues if available, falls back to Math.random.
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex: string[] = [];
  for (const b of bytes) hex.push(b.toString(16).padStart(2, '0'));
  return (
    hex.slice(0, 4).join('') + '-' +
    hex.slice(4, 6).join('') + '-' +
    hex.slice(6, 8).join('') + '-' +
    hex.slice(8, 10).join('') + '-' +
    hex.slice(10, 16).join('')
  );
}
